import { EMBED_STORAGE_PREFIX } from './embed';

// The reader moved from e-rhema.ch to reader.e-rhema.ch. localStorage is per origin, so the landing on the old
// origin hands the settings over in the URL fragment (reader-domain PRD, «Migration contract»).
export const MIGRATION_HASH_PREFIX = '#erh-migrate=';

export const MIGRATION_NOTICE_KEY = 'app-migration-notice-shown';

export interface MigrationPayload {
  ls: Record<string, string>;
  offline: string[];
}

const MIGRATABLE_KEYS: readonly string[] = [
  'appLanguage',
  'app-theme',
  'app-theme-variant',
  'app-advanced-mode',
  'app-show-icon-labels',
  'app-resource-visibility',
  'comparison-view-config',
  'comparison-view-sync-mode',
  'comparison-view-active-viewer'
];

// Per-section keys end or contain the section's viewer id (bible-search, interlinear-prefs, viewer-transliteration).
const MIGRATABLE_KEY_PATTERNS: readonly RegExp[] = [
  /^bible-search-state-.+$/,
  /^interlinear-(alignment-mode|target-translation|original-translation)-.+$/,
  /^viewer-.+-show-translit-annotations$/
];

export function isMigratableKey(key: string): boolean {
  return (
    !key.startsWith(EMBED_STORAGE_PREFIX) &&
    (MIGRATABLE_KEYS.includes(key) || MIGRATABLE_KEY_PATTERNS.some((pattern) => pattern.test(key)))
  );
}

// `<prefix><base64url, no padding, of UTF-8 JSON { v: 1, ls, offline? }>`; anything else is null.
export function readMigrationPayload(hash: string): MigrationPayload | null {
  if (!hash.startsWith(MIGRATION_HASH_PREFIX)) return null;
  try {
    const base64 = hash.slice(MIGRATION_HASH_PREFIX.length).replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
    const data = JSON.parse(new TextDecoder().decode(bytes));
    if (data?.v !== 1 || typeof data.ls !== 'object' || data.ls === null || Array.isArray(data.ls)) return null;
    const ls: Record<string, string> = {};
    for (const [key, value] of Object.entries(data.ls)) {
      if (typeof value === 'string' && isMigratableKey(key)) ls[key] = value;
    }
    const offline: string[] = Array.isArray(data.offline)
      ? data.offline.filter((id: unknown) => typeof id === 'string')
      : [];
    return { ls, offline };
  } catch {
    return null;
  }
}
